import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { BookOpen, ExternalLink } from 'lucide-react'
import { SocialCitationPost, SocialPostEngagement } from '@digital-twins/shared-types'
import Link from '@/routing/Link'
import { Button } from '@/components/ui/button'
import { Text } from '@/components/ui/Text'
import { SocialPlatformIcon } from './SocialPlatformIcon'
import { socialCitationQuote } from './SocialCitationPostMeta'
import { EMBED_SIZE_CLASS } from './SocialPostDetail'
import { formatSocialCount, formatSocialDate, SOCIAL_PLATFORM_LABELS } from './socialFormat'

interface SocialCitationSourceProps {
  post: SocialCitationPost
  citationLabel: string
  /** The digest in the Library, for "Open in Library". */
  libraryHref: string | null
  /** The cited passage; shown apart when it is not just the post text. */
  snippet?: string
}

const ENGAGEMENT_KEYS: Array<keyof SocialPostEngagement> = ['likes', 'comments', 'shares', 'views']

/** A cited social post shown as the post itself: the platform embed, its text and reach. */
export function SocialCitationSource(props: SocialCitationSourceProps) {
  const { post, citationLabel, libraryHref, snippet } = props
  const { t, i18n } = useTranslation('app')
  const [hasImageFailed, setHasImageFailed] = useState(false)

  const author = post.authorHandle
    ? `@${post.authorHandle}`
    : t('socialListening.detail.unknownAuthor')
  const date = formatSocialDate(post.publishedAt, i18n.language)
  const usedPassage = socialCitationQuote(post.text, snippet) ?? ''
  const hasUsedPassage = usedPassage !== ''
  const engagement = ENGAGEMENT_KEYS.flatMap((key) => {
    const value = post.engagement?.[key]
    if (value == null) {
      return []
    }
    return [
      t(`socialListening.detail.engagement.${key}`, {
        count: value,
        value: formatSocialCount(value, i18n.language),
      }),
    ]
  })

  return (
    <div className="flex min-h-0 flex-1 flex-col" data-testid="thread-source-view">
      <div className="space-y-3 border-b border-border px-5 py-4 text-left">
        <div className="flex items-center gap-3">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-muted">
            <SocialPlatformIcon platform={post.platform} className="h-4 w-4" />
          </span>
          <div className="min-w-0">
            <h2
              className="truncate font-serif text-xl font-normal leading-snug"
              data-testid="thread-source-title"
            >
              {author}
            </h2>
            <Text size="xs" color="muted">
              {[citationLabel, SOCIAL_PLATFORM_LABELS[post.platform], date, post.trackerName]
                .filter(Boolean)
                .join(' · ')}
            </Text>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button asChild variant="outline" size="sm" className="h-8">
            <a
              href={post.url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="thread-source-open-post"
            >
              <ExternalLink className="mr-1.5 h-3.5 w-3.5" />
              {t('socialListening.citation.openPost')}
            </a>
          </Button>
          {libraryHref ? (
            <Button asChild variant="outline" size="sm" className="h-8">
              <Link href={libraryHref} data-testid="thread-source-open-library">
                <BookOpen className="mr-1.5 h-3.5 w-3.5" />
                {t('threads.detail.openInLibrary')}
              </Link>
            </Button>
          ) : null}
        </div>
      </div>

      <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-5 py-4">
        {hasUsedPassage ? (
          <section
            className="space-y-1 rounded-xl border-l-2 border-primary bg-primary/5 px-3 py-2"
            data-testid="thread-source-social-used"
          >
            <Text as="p" size="xs" color="muted" weight="medium">
              {t('socialListening.citation.usedInAnswer')}
            </Text>
            <Text size="sm" className="whitespace-pre-line leading-relaxed">
              {usedPassage}
            </Text>
          </section>
        ) : null}
        {post.embedUrl ? (
          <iframe
            src={post.embedUrl}
            title={t('socialListening.detail.previewTitle', { author })}
            allow="autoplay; encrypted-media; picture-in-picture; fullscreen"
            referrerPolicy="strict-origin-when-cross-origin"
            className={`mx-auto block w-full rounded-xl border border-border bg-background ${EMBED_SIZE_CLASS[post.platform]}`}
            data-testid="thread-source-post-embed"
          />
        ) : post.thumbnailUrl && !hasImageFailed ? (
          <img
            src={post.thumbnailUrl}
            alt=""
            referrerPolicy="no-referrer"
            onError={() => setHasImageFailed(true)}
            className="w-full rounded-xl border border-border object-cover"
          />
        ) : null}
        {post.text ? (
          <Text size="sm" className="whitespace-pre-line leading-relaxed">
            {post.text}
          </Text>
        ) : null}
        {engagement.length > 0 ? (
          <Text as="p" size="xs" color="muted">
            {engagement.join(' · ')}
          </Text>
        ) : null}
      </div>
    </div>
  )
}
